<template>
    <v-container fluid class="team-page px-0 py-0 page-shell">
        <div v-if="isLoading" class="team-loading-screen">
            <v-progress-circular indeterminate size="56" width="4" color="#ac2f37" />
            <div class="team-loading-text">Φόρτωση περιεχομένου...</div>
        </div>
        <template v-else>
            <v-row v-if="heroImage" no-gutters class="">
                <v-col cols="12">
                    <img
                        height="auto"
                        width="100%"
                        :src="heroImage"
                        class="news-secondary-image"
                        aspect-ratio="1"
                    >
                </v-col>
            </v-row>
            <v-card class="team-tab-card mt-4 mt-md-6" outlined>
                <v-tabs v-model="activeTab" color="#223152" show-arrows class="team-tabs">
                    <v-tab v-if="isSportType">Ομάδα</v-tab>
                    <v-tab :disabled="!sportArticles.length">Άρθρα</v-tab>
                    <v-tab>Πληροφορίες</v-tab>
                    <v-tab :disabled="!pageGalleryImages.length">Gallery</v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" class="team-tab-items">
                    <v-tab-item v-if="isSportType">
                        <div class="tab-pane">
                            <team-roster-section
                                v-if="Number(sportId) === 2"
                                :members="rosterMembers"
                                :default-player-image="defaultPlayerImage"
                                :member-image-resolver="getMemberImage"
                            />

                            <team-standings-section
                                v-if="Number(sportId) === 2"
                                :standings="standings"
                                :standings-stat-columns="standingsStatColumns"
                                :is-floisvos-team="isFloisvosTeam"
                                :get-form-token-class="getFormTokenClass"
                            />

                            <team-schedule-section
                                v-if="Number(sportId) === 2"
                                :schedule="schedule"
                            />
                        </div>
                    </v-tab-item>

                    <v-tab-item>
                        <div class="tab-pane">
                            <v-row v-if="sportArticles.length" class="mx-0">
                                <v-col v-for="article in pagedSportArticles" :key="article.id" cols="12" md="6">
                                    <article-card :article="article" />
                                </v-col>
                            </v-row>
                            <v-row v-if="sportArticles.length > 0" class="mx-0 mt-2" justify="center">
                                <v-col cols="12" class="d-flex justify-center">
                                    <v-pagination
                                        :value="sportArticlesPage"
                                        :length="sportArticlesTotalPages"
                                        :total-visible="$vuetify.breakpoint.mobile ? 5 : 7"
                                        @input="handleSportArticlesPageChange"
                                    />
                                </v-col>
                            </v-row>
                            <v-alert v-else text type="info" class="mt-4">
                                Δεν υπάρχουν άρθρα για αυτό το τμήμα.
                            </v-alert>
                        </div>
                    </v-tab-item>

                    <v-tab-item>
                        <div class="tab-pane info-pane">
                            <div v-if="sportPages.length">
                                <div v-for="page in sportPages" :key="page.id" class="info-block">
                                    <h3 class="info-title">{{ page.title || (sportTitle ? `Πληροφορίες ${sportTitle}` : 'Πληροφορίες') }}</h3>
                                    <div class="info-content" v-html="page.description || ''" />
                                </div>
                            </div>
                            <v-alert v-else text type="info" class="mt-4">
                                Δεν υπάρχουν πληροφορίες για αυτό το τμήμα.
                            </v-alert>
                        </div>
                    </v-tab-item>

                    <v-tab-item>
                        <div class="tab-pane">
                            <v-row v-if="pageGalleryImages.length" class="mx-0">
                                <v-col
                                    v-for="(image, index) in pageGalleryImages"
                                    :key="`page-gallery-${image.pageId || 'na'}-${index}`"
                                    cols="12"
                                    sm="6"
                                    md="4"
                                    class="team-gallery-col"
                                >
                                    <v-card outlined class="team-gallery-card">
                                        <v-img
                                            :src="getPageGalleryImage(image)"
                                            :alt="image.description || `Gallery image ${index + 1}`"
                                            class="team-gallery-image"
                                            contain
                                            @click="openGalleryPreview(image, index)"
                                        />
                                    </v-card>
                                </v-col>
                            </v-row>
                            <v-alert v-else text type="info" class="mt-4">
                                Δεν υπάρχει gallery για αυτό το τμήμα.
                            </v-alert>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-card>

            <v-dialog v-model="isGalleryPreviewOpen" max-width="1100" content-class="team-gallery-preview-dialog" @click:outside="closeGalleryPreview">
                <v-card class="team-gallery-preview-card">
                    <div class="team-gallery-preview-toolbar">
                        <v-spacer />
                        <v-btn icon @click="closeGalleryPreview">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </div>
                    <v-img
                        :src="previewImageSrc"
                        :alt="previewImageAlt"
                        class="team-gallery-preview-image"
                        contain
                    />
                </v-card>
            </v-dialog>
        </template>
    </v-container>
</template>

<script>
    import defaultPlayerImage from '@/assets/default_player.png'
    import ArticleCard from '@/components/ArticleCard'
    import TeamRosterSection from '@/components/team/TeamRosterSection.vue'
    import TeamStandingsSection from '@/components/team/TeamStandingsSection.vue'
    import TeamScheduleSection from '@/components/team/TeamScheduleSection.vue'
    import articleService from '@/services/article'
    import pageService from '@/services/page'
    import sportService from '@/services/sport'
    import teamService from '@/services/team'

    export default {
        components: {
            ArticleCard,
            TeamRosterSection,
            TeamStandingsSection,
            TeamScheduleSection
        },
        data() {
            return {
                defaultPlayerImage,
                isLoading: true,
                activeTab: 0,
                heroImage: '',
                sportId: null,
                sportTitle: '',
                sportType: '',
                preferredStandingsColumns: [
                    { key: 'mp', label: 'MP', aliases: ['mp', 'played', 'matches', 'ag'], labelAliases: ['mp', 'αγ', 'αγωνες'] },
                    { key: 'points', label: 'Pts', aliases: ['points', 'pts', 'point', 'bath'], labelAliases: ['pts', 'πόντοι', 'βαθμοι', 'βαθμοί'] },
                    { key: 'w', label: 'W', aliases: ['w', 'wins', 'won', 'n'], labelAliases: ['w', 'ν', 'νικες', 'νίκες'] },
                    { key: 'l', label: 'L', aliases: ['l', 'losses', 'lost', 'h'], labelAliases: ['l', 'η', 'ηττες', 'ήττες'] },
                    { key: 'sw', label: 'SW', aliases: ['sw', 'setswon', 'sets_won'], labelAliases: ['sw'] },
                    { key: 'sl', label: 'SL', aliases: ['sl', 'setslost', 'sets_lost'], labelAliases: ['sl'] },
                    { key: 'sd', label: 'SD', aliases: ['sd', 'setdiff', 'set_difference'], labelAliases: ['sd'] }
                ],
                rosterMembers: [],
                standings: [],
                standingsStatColumns: [],
                standingsSectionName: '',
                schedule: [],
                sportArticlesPage: 1,
                sportArticlesPageSize: 6,
                sportArticles: [],
                sportPages: [],
                isGalleryPreviewOpen: false,
                previewImageSrc: '',
                previewImageAlt: ''
            }
        },
        computed: {
            isSportType() {
                return this.sportType === 'sport'
            },
            pagedSportArticles() {
                const start = (this.sportArticlesPage - 1) * this.sportArticlesPageSize
                return this.sportArticles.slice(start, start + this.sportArticlesPageSize)
            },
            sportArticlesTotalPages() {
                return Math.max(Math.ceil(this.sportArticles.length / this.sportArticlesPageSize), 1)
            },
            pageGalleryImages() {
                return this.sportPages.reduce((acc, page) => {
                    const gallery = page && Array.isArray(page.imageGallery) ? page.imageGallery : []
                    const normalized = gallery
                        .filter(image => image && (image.imageName || (image.image && image.image.imageName)))
                        .map(image => ({
                            ...image,
                            pageId: page && page.id ? page.id : null,
                            pageTitle: page && page.title ? page.title : ''
                        }))

                    return acc.concat(normalized)
                }, [])
            }
        },
        watch: {
            '$route.params.team': {
                immediate: true,
                handler() {
                    const routeParam = decodeURIComponent(String(this.$route.params.team || '')).trim()

                    this.activeTab = 0
                    this.isLoading = true
                    this.findSportByRouteParam(routeParam)
                        .then(sport => {
                            this.sportType = sport && sport.type ? sport.type : ''
                        })
                        .catch(() => {
                            this.sportType = ''
                        })
                        .then(() => this.fetchTeamData())
                }
            }
        },
        methods: {
            fetchTeamData() {
                this.isLoading = true
                const routeParam = decodeURIComponent(String(this.$route.params.team || '')).trim()
                const isSport = this.sportType === 'sport'

                return Promise.all([
                    this.fetchSportSectionData(routeParam),
                    isSport && this.sportId === 2 ? teamService.getStandings() : Promise.resolve(null),
                    isSport && this.sportId === 2 ? teamService.getSchedule() : Promise.resolve(null)
                ]).then(([, standingsResp, scheduleResp]) => {
                    const standingsSection = this.extractStandingsSection(standingsResp && standingsResp.data)
                    const standingsRows = standingsSection && Array.isArray(standingsSection.rows) ? standingsSection.rows : []
                    const scheduleRows = this.extractCollection(scheduleResp && scheduleResp.data)

                    this.standingsSectionName = standingsSection && standingsSection.name ? standingsSection.name : ''
                    this.standingsStatColumns = this.extractStandingColumns(standingsRows)
                    this.standings = standingsRows.map((row, index) => this.normalizeStanding(row, index))
                    this.schedule = scheduleRows
                        .slice()
                        .sort((a, b) => this.getScheduleTimestamp(b) - this.getScheduleTimestamp(a))
                        .map(row => this.normalizeSchedule(row))
                }).catch(() => {
                    this.heroImage = ''
                    this.sportId = null
                    this.sportTitle = routeParam
                    this.rosterMembers = []
                    this.sportArticlesPage = 1
                    this.sportArticles = []
                    this.sportPages = []
                    this.standings = []
                    this.standingsStatColumns = []
                    this.standingsSectionName = ''
                    this.schedule = []
                }).finally(() => {
                    this.isLoading = false
                })
            },
            fetchSportSectionData(routeParam) {
                return this.findSportByRouteParam(routeParam)
                    .then(sport => {
                        this.sportId = sport && sport.id ? sport.id : null
                        this.sportTitle = sport && sport.title ? sport.title : routeParam
                        this.heroImage = this.getSportHeroImage(sport)

                        const normalizedPlayers = this.normalizePlayers(sport)
                        this.rosterMembers = normalizedPlayers
                        this.sportArticlesPage = 1
                        this.sportArticles = this.normalizeSportArticles(sport)

                        return Promise.all([
                            this.sportArticles.length ? Promise.resolve(this.sportArticles) : this.fetchSportArticlesBySportId(this.sportId),
                            this.fetchSportPagesBySportId(this.sportId)
                        ])
                    })
                    .then(([articles, pages]) => {
                        this.sportArticlesPage = 1
                        this.sportArticles = articles
                        this.sportPages = pages
                    })
                    .catch(() => {
                        this.heroImage = ''
                        this.sportId = null
                        this.sportTitle = routeParam
                        this.rosterMembers = []
                        this.sportArticlesPage = 1
                        this.sportArticles = []
                        this.sportPages = []
                    })
            },
            handleSportArticlesPageChange(nextPage) {
                this.sportArticlesPage = nextPage
            },
            findSportByRouteParam(routeParam) {
                const asNumber = Number(routeParam)
                const isNumericParam = !Number.isNaN(asNumber) && String(routeParam) !== ''

                if (!isNumericParam) {
                    return Promise.resolve(null)
                }

                return sportService.getSport(asNumber)
                    .then(detailResp => (detailResp && detailResp.data && detailResp.data.sport ? detailResp.data.sport : null))
            },
            fetchSportArticlesBySportId(sportId) {
                if (!sportId) {
                    return Promise.resolve([])
                }

                return articleService.getArticles(1, 200, sportId)
                    .then(resp => {
                        const rows = this.extractCollection(resp && resp.data)
                        return rows
                            .filter(article => this.getEntitySportId(article) === Number(sportId))
                            .map(article => this.normalizeArticle(article))
                    })
                    .catch(() => [])
            },
            fetchSportPagesBySportId(sportId) {
                if (!sportId) {
                    return Promise.resolve([])
                }

                return pageService.getPagesBySportId(sportId, 1, 0)
                    .then(resp => this.extractCollection(resp && resp.data))
                    .catch(() => [])
            },
            getPageGalleryImage(image) {
                if (!image) {
                    return ''
                }

                if (image.imageName) {
                    return `${process.env.VUE_APP_GOOGLE_BUCKET_URL}${image.imageName}`
                }

                if (image.image && image.image.imageName) {
                    return `${process.env.VUE_APP_GOOGLE_BUCKET_URL}${image.image.imageName}`
                }

                return ''
            },
            openGalleryPreview(image, index) {
                const src = this.getPageGalleryImage(image)
                if (!src) {
                    return
                }

                this.previewImageSrc = src
                this.previewImageAlt = (image && image.description) ? image.description : `Gallery image ${index + 1}`
                this.isGalleryPreviewOpen = true
            },
            closeGalleryPreview() {
                this.isGalleryPreviewOpen = false
            },
            normalizeText(value) {
                return String(value || '')
                    .trim()
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
            },
            getEntitySportId(entity) {
                if (!entity) {
                    return null
                }

                if (entity.sport && typeof entity.sport === 'object' && entity.sport.id !== undefined && entity.sport.id !== null) {
                    return Number(entity.sport.id)
                }

                if (entity.sport !== undefined && entity.sport !== null && entity.sport !== '') {
                    return Number(entity.sport)
                }

                return null
            },
            getSportHeroImage(sport) {
                const imageName = sport && sport.image && sport.image.imageName ? sport.image.imageName : ''
                if (!imageName) {
                    return ''
                }

                return `${process.env.VUE_APP_GOOGLE_BUCKET_URL}${imageName}`
            },
            normalizePlayers(sport) {
                const rows = sport && Array.isArray(sport.players) ? sport.players : []
                const objectRows = rows.filter(player => player && typeof player === 'object')

                return objectRows.map((player, index) => {
                    const firstName = String(player.firstName || '').trim()
                    const lastName = String(player.lastName || '').trim()
                    const fullName = `${firstName} ${lastName}`.trim() || String(player.name || '').trim() || `Παίκτης ${index + 1}`

                    return {
                        id: player.id || index,
                        number: player.number !== undefined && player.number !== null ? player.number : '-',
                        name: fullName,
                        position: player.position || '-',
                        height: player.height || '-',
                        nationality: player.nationality || '-',
                        birthDate: player.birthDate || null,
                        role: player.role || 'player',
                        image: player.image && player.image.imageName ? `${process.env.VUE_APP_GOOGLE_BUCKET_URL}${player.image.imageName}` : ''
                    }
                })
            },
            isCoachRole(role) {
                const normalized = this.normalizeText(role)
                return normalized.includes('coach') || normalized.includes('προπονητ')
            },
            normalizeSportArticles(sport) {
                const rows = sport && Array.isArray(sport.articles) ? sport.articles : []
                const objectRows = rows.filter(article => article && typeof article === 'object')

                return objectRows
                    .map(article => this.normalizeArticle(article))
                    .sort((a, b) => new Date(b.articleDate || 0).getTime() - new Date(a.articleDate || 0).getTime())
            },
            normalizeArticle(article) {
                if (!article || typeof article !== 'object') {
                    return article
                }

                if (!article.image || typeof article.image !== 'object') {
                    return {
                        ...article,
                        image: article.imageName ? { imageName: article.imageName } : null
                    }
                }

                return article
            },
            getMemberImage(member) {
                return member && member.image ? member.image : ''
            },
            extractStandingsSection(payload) {
                const groups = payload && Array.isArray(payload.standings)
                    ? payload.standings
                    : (payload && payload.data && Array.isArray(payload.data.standings) ? payload.data.standings : [])

                if (!groups.length) {
                    return null
                }

                // Prefer regular season; otherwise fallback to the first standings group.
                const regular = groups.find(group => {
                    const name = String((group && group.name) || '').toLowerCase()
                    return name.includes('κανονικ') || name.includes('regular')
                })

                return regular || groups[0]
            },
            extractStandingColumns(rows) {
                return this.preferredStandingsColumns.map(column => ({
                    key: column.key,
                    label: column.label,
                    aliases: column.aliases || [],
                    labelAliases: column.labelAliases || []
                }))
            },
            getStatsMap(stats) {
                if (!Array.isArray(stats)) {
                    return {}
                }

                return stats.reduce((acc, stat) => {
                    const key = String(stat && stat.key ? stat.key : '').toLowerCase()
                    if (key) {
                        acc[key] = stat.value !== undefined && stat.value !== null ? stat.value : '-'
                    }
                    return acc
                }, {})
            },
            getStatValueByColumn(stats, column) {
                if (!Array.isArray(stats)) {
                    return '-'
                }

                const keyHints = [column.key].concat(column.aliases || []).map(hint => String(hint).toLowerCase())
                const labelHints = [column.label].concat(column.labelAliases || []).map(hint => String(hint).toLowerCase())

                const found = stats.find((stat) => {
                    const statKey = String(stat && stat.key ? stat.key : '').toLowerCase()
                    const statLabel = String(stat && stat.label ? stat.label : '').toLowerCase()
                    return keyHints.includes(statKey) || labelHints.includes(statLabel)
                })

                if (!found) {
                    return '-'
                }

                return found.value !== undefined && found.value !== null ? found.value : '-'
            },
            getRowStatsValues(row) {
                return this.standingsStatColumns.reduce((acc, column) => {
                    acc[column.key] = this.getStatValueByColumn(row.stats, column)
                    return acc
                }, {})
            },
            getFormTokens(form) {
                const raw = Array.isArray(form) ? form.join(' ') : String(form || '')
                return raw
                    .replace(/,/g, ' ')
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 5)
            },
            getFormTokenClass(token) {
                const normalized = String(token || '').toUpperCase()
                if (normalized === 'W') {
                    return 'form-token-win'
                }
                if (normalized === 'L') {
                    return 'form-token-loss'
                }
                return 'form-token-neutral'
            },
            normalizeTeamName(name) {
                return String(name || '')
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
            },
            isFloisvosTeam(teamName) {
                const normalized = this.normalizeTeamName(teamName)
                return normalized.includes('φλοισβ')
            },
            resolveTeamLogo(urlPath) {
                if (!urlPath) {
                    return ''
                }

                if (String(urlPath).startsWith('http://') || String(urlPath).startsWith('https://')) {
                    return urlPath
                }

                if (String(urlPath).startsWith('/')) {
                    return `https://www.gazzetta.gr${urlPath}`
                }

                return urlPath
            },
            extractCollection(payload) {
                if (Array.isArray(payload)) {
                    return payload
                }

                if (!payload || typeof payload !== 'object') {
                    return []
                }

                const directKeys = ['data', 'items', 'rows', 'standings', 'schedule', 'matches']
                for (const key of directKeys) {
                    if (Array.isArray(payload[key])) {
                        return payload[key]
                    }
                }

                if (payload.data && typeof payload.data === 'object') {
                    for (const key of directKeys) {
                        if (Array.isArray(payload.data[key])) {
                            return payload.data[key]
                        }
                    }
                }

                return []
            },
            normalizeStanding(row, index) {
                const position = row.rank || row.position || row.place || row.thesi || index + 1
                const team = row.team_name || row.team || row.name || row.club || row.omada || '-'
                const teamLogo = this.resolveTeamLogo(row.team_logo || row.logo || row.teamLogo || '')
                const statsValues = this.getRowStatsValues(row)
                const statsMap = this.getStatsMap(row.stats)
                const formSource = row.form || row.last5 || row.form_last_5 || row.last_five || statsMap.form || statsMap.last5 || statsMap.form_last_5 || ''
                const formTokens = this.getFormTokens(formSource)

                return { position, team, teamLogo, statsValues, formTokens }
            },
            getScheduleTimestamp(row) {
                const numericTimestamp = Number(row && row.timestamp)
                if (!Number.isNaN(numericTimestamp) && numericTimestamp > 0) {
                    return numericTimestamp
                }

                const dateString = String((row && (row.match_date || row.date || row.matchDate)) || '')
                const timeString = String((row && (row.match_time || row.time || row.matchTime)) || '00:00')

                const dateMatch = dateString.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/)
                if (!dateMatch) {
                    return 0
                }

                const day = Number(dateMatch[1])
                const month = Number(dateMatch[2]) - 1
                const year = Number(dateMatch[3])
                const [hoursRaw, minutesRaw] = timeString.split(':')
                const hours = Number(hoursRaw) || 0
                const minutes = Number(minutesRaw) || 0

                return Math.floor(new Date(year, month, day, hours, minutes).getTime() / 1000)
            },
            normalizeSchedule(row) {
                const id = row.match_id || row.id || `${row.match_date || row.date}-${row.match_time || row.time}`
                const round = row.round_name || row.round || '-'
                const date = row.match_date || row.date || row.matchDate || row.day || '-'
                const time = row.match_time || row.time || row.matchTime || row.hour || '-'
                const home = (row.home_team && row.home_team.name) || row.home || row.homeTeam || row.teamA || row.host || '-'
                const away = (row.away_team && row.away_team.name) || row.away || row.awayTeam || row.teamB || row.guest || '-'
                const homeLogo = this.resolveTeamLogo(row.home_team && row.home_team.logo)
                const awayLogo = this.resolveTeamLogo(row.away_team && row.away_team.logo)
                const homeScore = row.home_team && row.home_team.score !== undefined && row.home_team.score !== null ? row.home_team.score : '-'
                const awayScore = row.away_team && row.away_team.score !== undefined && row.away_team.score !== null ? row.away_team.score : '-'

                return { id, round, date, time, home, away, homeLogo, awayLogo, homeScore, awayScore }
            }
        }
    }
</script>

<style scoped>
.team-page {
    width: 100%;
}

.team-loading-screen {
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
}

.team-loading-text {
    color: #223152;
    font-weight: 700;
    letter-spacing: 0.02em;
}

.team-tab-card {
    background: rgba(255, 255, 255, 0.94);
}

.team-tab-card .v-tab {
    text-transform: capitalize !important;
}

.team-tab-items {
    background: transparent;
}

.team-tabs::v-deep .v-slide-group__content {
    justify-content: center;
}

.tab-pane {
    padding: 14px;
}

.info-pane {
    text-align: left;
}

.info-block {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid #e3e7ee;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
}

.info-block:last-child {
    margin-bottom: 0;
}

.info-title {
    margin: 0 0 10px;
    color: #223152;
}

.info-content {
    color: #4b5970;
    line-height: 1.7;
}

.team-gallery-col {
    padding: 8px;
}

.team-gallery-card {
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
}

.team-gallery-image {
    width: 100%;
    height: clamp(170px, 24vw, 280px);
    background: #f8fafc;
    cursor: zoom-in;
}

.team-gallery-preview-card {
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
}

.team-gallery-preview-toolbar {
    display: flex;
    align-items: center;
    padding: 6px 8px;
}

.team-gallery-preview-image {
    width: 100%;
    height: min(80vh, 820px);
    background: #f8fafc;
}

@media only screen and (max-width: 600px) {
    .tab-pane {
        padding: 8px;
    }

    .team-gallery-col {
        padding: 4px;
    }
}
</style>
